import { AuthenticationDetails, CognitoUser } from "amazon-cognito-identity-js";
import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope, faLock, faEye, faEyeSlash, faCircleQuestion } from "@fortawesome/free-solid-svg-icons";

import UserPool from "../../utils/UserPool";
import config from "../../config/config.json";
import "./SetPassword.css";

const SetPassword = () => {
  let [searchParams] = useSearchParams();
  const code = searchParams.get("email");
  const navigate = useNavigate();

  const [email, setEmail] = useState(code?.replace(/ /g, "+") || "");
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [message, setMessage] = useState("");
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleResetPassword = (event) => {
    event.preventDefault();
    setMessage("");
    
    if (newPassword === oldPassword) {
      setMessage("New password can't be the same as the old password.");
      return;
    }
    
    if (newPasswordConfirm !== newPassword) {
      setMessage("New password and confirm password don't match.");
      return;
    }
    
    setIsLoading(true);
    resetPassword(email, oldPassword, newPassword);
  };

  const resetPassword = (email, oldPassword, newPassword) => {
    var cognitoUser = createCognitoUser(email);
    const authenticationDetails = new AuthenticationDetails({
      Username: email,
      Password: oldPassword,
    });
    cognitoUser = createCognitoUser(email);
    cognitoUser.authenticateUser(authenticationDetails, {
      onSuccess: () => {
        console.log("login successful from resetPassword");
      },
      onFailure: () => {
        setIsLoading(false);
        setMessage("Username and old password are incorrect.");
      },
      newPasswordRequired: (userAttributes) => {
        delete userAttributes.email;
        delete userAttributes.email_verified;
        delete userAttributes.phone_number_verified;

        userAttributes.name = authenticationDetails.username;
        cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
          onSuccess: () => {
            setIsLoading(false);
            localStorage.clear();
            navigate("/");
          },
          onFailure: (err) => {
            setIsLoading(false);
            switch (err.code) {
              case "InvalidPasswordException":
                setMessage(
                  "New password is not strong enough. Your password must be 8 characters long and contain at least one number, special character, uppercase letter, and lowercase letter"
                );
                break;
              default:
                setMessage(err.message);
            }
            console.log(err);
          },
        });
      },
    });
  };

  const createCognitoUser = (email) => {
    return new CognitoUser({
      Username: email,
      Pool: UserPool,
    });
  };

  return (
    <div className="setpassword-container">
      {/* Internet Explorer Warning */}
      <div className="px-3 pt-4 mt-4 d-none" id="iesection">
        <h5>
          <strong>Sorry!</strong> Internet Explorer is no longer Supported. 
          Please use Chrome, Safari, Firefox or Edge browser.
        </h5>
      </div>
      
      {/* Background Image with Overlay */}
      <div className="setpassword-background">
        <div className="setpassword-overlay"></div>
      </div>

      {/* Content Wrapper */}
      <div className="setpassword-content">
        {/* Left Panel - Branding Info */}
        <div className="setpassword-info-panel">
          <div className="setpassword-brand-section">
            <div className="setpassword-logo-container">
              <img
                src={require("../../asset/image/logos/trial-main-box-logo.png")}
                alt="SMi Trial Logo"
                className="setpassword-brand-logo"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            
            <h1 className="setpassword-main-title">
              SMi Trial<sup>&trade;</sup>
            </h1>
            <h2 className="setpassword-subtitle">
              Protocol Compliance Management
            </h2>
            
            <div className="setpassword-features">
              <div className="setpassword-feature-badge">
                <span className="setpassword-feature-icon">✓</span>
                <span>Real-time Protocol Tracking</span>
              </div>
              <div className="setpassword-feature-badge">
                <span className="setpassword-feature-icon">✓</span>
                <span>Comprehensive Reporting</span>
              </div>
              <div className="setpassword-feature-badge">
                <span className="setpassword-feature-icon">✓</span>
                <span>Regulatory Compliance</span>
              </div>
            </div>

            <div className="setpassword-learn-more">
              <a 
                href="https://www.sciencemedia.com/trial" 
                target="_blank" 
                rel="noopener noreferrer"
                className="setpassword-learn-more-btn"
              >
                Learn More About SMi Trial
              </a>
            </div>
          </div>

          <div className="setpassword-footer">
            <p>&copy; {new Date().getFullYear()} Science Media. All Rights Reserved.</p>
            <div className="setpassword-footer-links">
              <a href="https://sciencemedia.com/privacy" target="_blank" rel="noopener noreferrer">
                Privacy
              </a>
              <span>•</span>
              <a href="#" onClick={(e) => e.preventDefault()}>
                Terms
              </a>
            </div>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="setpassword-form-panel">
          <div className="setpassword-form-card">
            <div className="setpassword-form-header">
              <img
                src={require(`../../asset/image/logos/${config.site.logo}`)}
                alt="Logo"
                className="setpassword-site-logo"
              />
              <h2 className="setpassword-form-title">Set New Password</h2>
              <p className="setpassword-form-subtitle">
                Please choose a new password for your account
              </p>
            </div>

            <form onSubmit={handleResetPassword} className="setpassword-form">
              {message && (
                <div className="setpassword-alert">
                  <span className="setpassword-alert-icon">⚠</span>
                  <p>{message}</p>
                </div>
              )}

              <div className="setpassword-form-group">
                <label htmlFor="emailInput" className="setpassword-label">
                  Email Address
                </label>
                <div className="setpassword-input-wrapper">
                  <FontAwesomeIcon icon={faEnvelope} className="setpassword-input-icon" />
                  <input
                    type="email"
                    id="emailInput"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="setpassword-input"
                    placeholder="Enter your email"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>

              <div className="setpassword-form-group">
                <label htmlFor="oldPasswordInput" className="setpassword-label">
                  Current Password
                </label>
                <div className="setpassword-input-wrapper">
                  <FontAwesomeIcon icon={faLock} className="setpassword-input-icon" />
                  <input
                    type={showOldPassword ? "text" : "password"}
                    id="oldPasswordInput"
                    value={oldPassword}
                    onChange={(e) => setOldPassword(e.target.value)}
                    className="setpassword-input"
                    placeholder="Enter your current password"
                    required
                    autoFocus
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="setpassword-password-toggle"
                    onClick={() => setShowOldPassword(!showOldPassword)}
                    tabIndex={-1}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={showOldPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className="setpassword-form-group">
                <label htmlFor="newPasswordInput" className="setpassword-label">
                  New Password
                </label>
                <div className="setpassword-input-wrapper">
                  <FontAwesomeIcon icon={faLock} className="setpassword-input-icon" />
                  <input
                    type={showNewPassword ? "text" : "password"}
                    id="newPasswordInput"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="setpassword-input"
                    placeholder="Enter your new password"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="setpassword-password-toggle"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    tabIndex={-1}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={showNewPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className="setpassword-form-group">
                <label htmlFor="confirmPasswordInput" className="setpassword-label">
                  Confirm New Password
                </label>
                <div className="setpassword-input-wrapper">
                  <FontAwesomeIcon icon={faLock} className="setpassword-input-icon" />
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPasswordInput"
                    value={newPasswordConfirm}
                    onChange={(e) => setNewPasswordConfirm(e.target.value)}
                    className="setpassword-input"
                    placeholder="Confirm your new password"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    className="setpassword-password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    tabIndex={-1}
                    disabled={isLoading}
                  >
                    <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                className={`setpassword-submit-btn ${isLoading ? "setpassword-loading" : ""}`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="setpassword-spinner"></span>
                    <span>Updating Password...</span>
                  </>
                ) : (
                  "Update Password"
                )}
              </button>
            </form>

            <div className="setpassword-help-section">
              <FontAwesomeIcon icon={faCircleQuestion} />
              <span>Need help? </span>
              <a href="mailto:help@smitrial.com" className="setpassword-help-link">
                Contact Support
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetPassword;
